"use client";
import React, { useState, useEffect, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import Input from "../common/input/input";
import Button from "../common/button";
import { useCheckUsernameExistsQuery } from "../../store/api/userApi";
import { useDebounce } from "../../hooks/debounce-hooks";
import { generateUsername } from "../../helpers";
import ProgressBar from "../common/progress-bar";
import LottieAnimation from "../common/animations";
import { CaretRight } from "../../icons";
import { clearAllState, removeAllCookies } from "../../helpers/storage";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../constants/routes";

const PersonalDetailsComp = () => {
  const navigate = useNavigate();
  const {
    control,
    formState: { errors, isSubmitting },
    watch,
    setError,
    setValue,
  }: any = useFormContext();
  const isMounted = useRef(false);
  const [isUsernameCheckInitialized, setIsUsernameCheckInitialized] =
    useState(false);

  const username = watch("username");
  const firstname = watch("first_name");
  const lastname = watch("last_name");
  const debouncedUsername = useDebounce(username, 500);
  const debouncedLastname = useDebounce(lastname, 500);
  const debouncedFirstname = useDebounce(firstname, 500);

  const [rightIcon, setRightIcon] = useState<string | null>(null);

  const {
    data: usernameCheckData,
    refetch,
    isFetching,
  } = useCheckUsernameExistsQuery(
    { username: debouncedUsername },
    { skip: !debouncedUsername },
  );

  useEffect(() => {
    if (isMounted.current) {
      if (debouncedUsername) {
        refetch();
        setIsUsernameCheckInitialized(true);
      }
    } else {
      isMounted.current = true;
    }
  }, [debouncedUsername]);

  useEffect(() => {
    const tryGenerateUniqueUsername = async (attempts: number) => {
      if (!firstname || !debouncedLastname || attempts >= 10) return;

      const newUsername = generateUsername(firstname, lastname);

      setValue("username", newUsername);

      if (isUsernameCheckInitialized) {
        await refetch();

        if (usernameCheckData?.data?.exists && attempts < 10) {
          tryGenerateUniqueUsername(attempts + 1);
        }
      }
    };

    if (firstname && debouncedLastname) {
      tryGenerateUniqueUsername(0);
    }
  }, [debouncedFirstname, debouncedLastname]);

  useEffect(() => {
    if (usernameCheckData?.data?.exists === false) {
      setRightIcon("valid");
      setError("username", { type: "manual", message: "" });
    } else if (!debouncedUsername) {
      setRightIcon(null);
    } else {
      setRightIcon("invalid");
      setError("username", {
        type: "manual",
        message: "Username already exists. Try another one",
      });
    }
  }, [usernameCheckData]);

  return (
    <div className="h-full flex flex-col justify-between">
      <div className="mt-0">
        <div className="flex items-center flow-root ">
          <div className="w-32 md:mb-10 mb-6 float-left">
            <div
              className="cursor-pointer mb-3 "
              onClick={() => {
                clearAllState();
                navigate(ROUTES.AUTH_PHONE_NUMBER);
                removeAllCookies();
              }}
            >
              <div className="rotate-180 transform origin-left ml-5">
                <CaretRight />
              </div>
            </div>
            <LottieAnimation lottieUrl="/animation/Logo.lottie" height="40px" />
          </div>

          <div className="mt-12 ml-4 w-1/5 float-right">
            <ProgressBar currentStep={1} totalSteps={5} />
          </div>
        </div>

        <div className="text-2.8xl text-white leading-10 font-bold mb-4">
          Complete your profile
        </div>
        <div className="text-2.8xl text-gray-200 leading-1 text-md mb-10">
          Tell us a little bit about yourself. Please be as accurate as possible
          so that we can curate a personalised experience for you.
        </div>
        <div className="">
          <div className="w-full mb-5">
            <Controller
              name="first_name"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange } }) => (
                <Input
                  id="first_name"
                  name="first_name"
                  customClassName="bg-[#262626] text-white px-3"
                  maxLength={30}
                  showError={true}
                  errorMessage={errors?.first_name?.message}
                  value={value}
                  onChange={onChange}
                  placeholder="First Name"
                />
              )}
            />
          </div>
          <div className="w-full mb-5">
            <Controller
              name="last_name"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange } }) => (
                <Input
                  id="last_name"
                  name="last_name"
                  customClassName="bg-[#262626] text-white px-3"
                  maxLength={30}
                  showError={true}
                  errorMessage={errors?.last_name?.message}
                  value={value}
                  onChange={onChange}
                  placeholder="Last Name"
                />
              )}
            />
          </div>
          <div className="w-full mb-5">
            <Controller
              name="username"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange } }) => (
                <Input
                  id="username"
                  name="username"
                  customClassName="bg-[#262626] text-white px-3"
                  maxLength={40}
                  showError={true}
                  errorMessage={errors?.username?.message}
                  value={value}
                  onChange={onChange}
                  placeholder="Username"
                  rightIcon={rightIcon}
                  isLoading={isFetching}
                />
              )}
            />
          </div>
          <div className="grid grid-cols-2 gap-3 mb-20">
            <div className="w-full ">
              <Controller
                name="gender"
                control={control}
                defaultValue=""
                rules={{ required: "Gender is required" }}
                render={({ field }) => (
                  <div className="bg-[#262626] rounded-xl-10 pl-3 pr-4">
                    <select
                      {...field}
                      id="gender"
                      className="bg-[#262626] text-white py-[15px] text-black outline-none w-full rounded-xl-10"
                    >
                      <option value="">Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Others">Others</option>
                    </select>
                  </div>
                )}
              />
              {errors?.gender && (
                <div className={`text-error text-xs mt-1`}>
                  {errors?.gender?.message}
                </div>
              )}
            </div>
            <div className="w-full">
              <Controller
                name="dob"
                control={control}
                rules={{ required: true }}
                render={({ field: { value, onChange } }) => (
                  <Input
                    id="dob"
                    name="dob"
                    customClassName="bg-[#262626] text-white px-3 py-3 hover:cursor-pointer"
                    maxLength={10}
                    showError={true}
                    errorMessage={errors?.dob?.message}
                    value={value}
                    onChange={onChange}
                    placeholder="Date of Birth"
                    type="date"
                    onClick={(e) => e.currentTarget.showPicker()}
                  />
                )}
              />
            </div>
          </div>
        </div>
      </div>
      <div>
        <Button
          isSubmitting={isSubmitting}
          customClasses="w-full mb-5 rounded-xl"
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default PersonalDetailsComp;
